import { gql } from '../common/functions/gql';
import { IBenefit } from '../container/account/benefits/components/interfaces/ICommon';
import { ILiveSession, IRx } from '../container/account/rx/components/interfaces/ICommon';
import { IUserProfile } from '../providers/user-provider';
import { safeGet } from '../utils/objectUtils';
import { AxiosInstance } from 'axios';
import { alertBannerType } from '../providers/alert-provider';
import { splunk_message } from '../common/splunk_constant';

const PRESCRIPTIONS_QUERY = gql`
  query ($forceCacheReload: Boolean!) {
    logInByGuid {
      prescriptions(forceCacheReload: $forceCacheReload) {
        active {
          prescriptionId
          status
          type
          expirationDate
        }
        expired {
          prescriptionId
          status
          type
          expirationDate
        }
      }
    }
  }
`;

const PENDING_CLSP_ORDER_QUERY = gql`
  query {
    hasPendingClspOrder
  }
`;

export interface UserDetailsDto {
  forceCacheReload: boolean;
}

export default class UserProfileService {
  visionBffClient: AxiosInstance;
  splunkLogger?: any;

  constructor(visionBffClient: AxiosInstance, splunkLogger?: any) {
    this.visionBffClient = visionBffClient;
    this.splunkLogger = splunkLogger;
  }

  async getBaseUserDetails(
    userDetailsDto: UserDetailsDto,
  ): Promise<IUserProfile | undefined | { error: string }> {
    try {
      const response = await this.visionBffClient({
        method: 'POST',
        url: `/api/v1/user`,
        data: { query: BASE_USER_QUERY, variables: userDetailsDto },
      });
      this.splunkLogger?.({
        logType: 'info',
        data: splunk_message.SUCCESS.BASE_USER_PROFILE_SUCCESS,
      });
      if (response.data.errors) {
        return {
          error: alertBannerType.USER_LOGIN_ERROR,
        };
      } else {
        const userData = safeGet(response, 'data.data', null);
        return userData;
      }
    } catch (err: any) {
      this.splunkLogger?.({
        logType: 'error',
        data: { ...splunk_message.ERROR.BASE_USER_PROFILE_ERROR, err },
      });
      return {
        error: alertBannerType.USER_LOGIN_ERROR,
      };
    }
  }

  async getExtendedUserDetails(
    userDetailsDto: UserDetailsDto,
  ): Promise<IUserProfile | undefined | { error: string }> {
    try {
      const response = await this.visionBffClient({
        method: 'POST',
        url: `/api/v1/user`,
        data: { query: EXTENDED_USER_QUERY, variables: userDetailsDto },
      });
      this.splunkLogger?.({
        logType: 'info',
        data: splunk_message.SUCCESS.EXTENDED_USER_PROFILE_SUCCESS,
      });
      if (response.data.errors) {
        return {
          error: alertBannerType.USER_EXTENDED_DETAILS_ERROR,
        };
      } else {
        const userData = safeGet(response, 'data.data', null);
        return userData;
      }
    } catch (err: any) {
      this.splunkLogger?.({
        logType: 'error',
        data: { ...splunk_message.ERROR.EXTENDED_USER_PROFILE_ERROR, err },
      });
      return {
        error: alertBannerType.USER_EXTENDED_DETAILS_ERROR,
      };
    }
  }

  async getPendingClspOrder(): Promise<boolean> {
    try {
      const response = await this.visionBffClient({
        method: 'POST',
        url: `/api/v1/user`,
        data: { query: PENDING_CLSP_ORDER_QUERY },
      });

      if (response.data.errors) {
        return false;
      }

      return Boolean(safeGet(response, 'data.data.hasPendingClspOrder', false));
    } catch (err: any) {
      this.splunkLogger?.({
        logType: 'error',
        data: { ...splunk_message.ERROR.PENDING_CLSP_ORDER_ERROR, err },
      });
      return false;
    }
  }

  async getPersonalNotifications(isGetNotification: boolean, msgId?: string): Promise<any> {
    const getNotificationQuery = {
      query: `query{
          getPersonalNotifications{
              messageId
              messageType
              messageBody{
                  jsonRTE
                  text
                  csTemplate
              },
              userActions
              params
              updatedAt
              messageCode
              messageTags
          }
        }   `,
    };
    const closeNotificationQuery = {
      query: `mutation ($messageId : String!){
      closePersonalNotification(messageId : $messageId) {
          messageId
          messageType
          messageBody{
              jsonRTE
              text
              csTemplate
          },
          userActions
          params
          updatedAt
          messageCode
          messageTags
      }
    } `,
      variables: { messageId: msgId },
    };

    try {
      const response = await this.visionBffClient({
        method: 'POST',
        url: `/api/v1/user`,
        data: isGetNotification ? getNotificationQuery : closeNotificationQuery,
      });
      this.splunkLogger?.({
        logType: 'info',
        data: splunk_message.SUCCESS.NOTIFICATION_SERVICE_SUCCESS,
      });
      return response;
    } catch (error) {
      this.splunkLogger?.({
        logType: 'error',
        data: { ...splunk_message.ERROR.NOTIFICATION_SERVICE_ERROR, error },
      });
      return error;
    }
  }
  async getBenefits(userDetailsDto: UserDetailsDto): Promise<
    | {
        benefits?: IBenefit[];
        [key: string]: any;
      }
    | { error: string }
  > {
    try {
      const response = await this.visionBffClient({
        method: 'GET',
        url: `/api/v1/benefits`,
        data: userDetailsDto,
      });
      this.splunkLogger?.({
        logType: 'info',
        data: splunk_message.SUCCESS.GET_BENEFITS_SUCCESS,
      });
      return response?.data;
    } catch (err: any) {
      console.log(err);
      this.splunkLogger?.({
        logType: 'error',
        data: { ...splunk_message.ERROR.GET_BENEFITS_ERROR, err },
      });
      if (err?.response?.status === 404) {
        return { error: 'getBenefits.404.error' };
      } else {
        return { error: 'getBenefits.standard.error' };
      }
    }
  }

  async getLiveSession(): Promise<any> {
    const query = {
      query: `
      query {
  getLiveSession{
        eg_pd{
                distance{
                    left
                    right
                }
                intermediate{
                    left
                    right
                }
                near{
                    left
                    right
                }
                isEmpty
                sourcePD
        }
        eg_rx{
        status
        type
        eyeglassRxType
        prescriptionId
        issueDate
        expirationDate
        prescribedBy
        prescriptionStrength
        prescriptionId
        leftEye {
            add
            axis
            bc
            color
            cyl
            dia
            model
            pd
            prisms {
                base
                prism
            }
            sku
            sph
        }
        rightEye {
            add
            axis
            bc
            color
            cyl
            dia
            model
            pd
            prisms {
                base
                prism
            }
            sku
            sph
        }
    }
        cl_rx{
        status
        type
        eyeglassRxType
        prescriptionId
        issueDate
        expirationDate
        prescribedBy
        prescriptionStrength
        prescriptionId
        leftEye {
            add
            axis
            bc
            color
            cyl
            dia
            model
            pd
            prisms {
                base
                prism
            }
            skus{
              packSize
              colour
              sku
              webEnabled
            }
            sku
            sph
        }
        rightEye {
            add
            axis
            bc
            color
            cyl
            dia
            model
            pd
            prisms {
                base
                prism
            }
            sku
            skus{
              packSize
              colour
              sku
              webEnabled
            }
            sph
        }
    }

    }
    }
        `,
    };

    try {
      const response = await this.visionBffClient({
        method: 'POST',
        url: `/api/v1/user`,
        data: query,
      });
      this.splunkLogger?.({
        logType: 'info',
        data: splunk_message.SUCCESS.GET_LIVE_SESSION_SUCCESS,
      });
      const liveSessionData = safeGet(response, 'data', null);
      return liveSessionData;
    } catch (error) {
      this.splunkLogger?.({
        logType: 'error',
        data: { ...splunk_message.ERROR.GET_LIVE_SESSION_ERROR, error },
      });
      return error;
    }
  }

  async setLiveSession(liveSessionData: ILiveSession): Promise<any> {
    try {
      const query = gql`
        mutation ($liveSessionData: SetLiveSessionData!) {
          setLiveSession(liveSessionData: $liveSessionData)
        }
      `;

      const variables = {
        liveSessionData,
      };

      const request = { query, variables };
      let response = await this.visionBffClient.post('/api/v1/user', request);
      this.splunkLogger?.({
        logType: 'info',
        data: splunk_message.SUCCESS.SET_LIVE_SESSION_SUCCESS,
      });
      return response.data;
    } catch (error) {
      this.splunkLogger?.({
        logType: 'error',
        data: { ...splunk_message.ERROR.SET_LIVE_SESSION_ERROR, error },
      });
      return error;
    }
  }

  async setPdInLiveSession(eg_pd: number): Promise<any> {
    const query = {
      query: `
          mutation ($pdValue: Float){
            setPDforEyeglasses(
                pdValue : $pdValue
            )
       }
`,
      variables: {
        pdValue: eg_pd,
      },
    };

    try {
      const response = await this.visionBffClient({
        method: 'POST',
        url: `/api/v1/user`,
        data: query,
      });
      this.splunkLogger?.({
        logType: 'info',
        data: splunk_message.SUCCESS.SET_PD_SUCCESS,
      });
      return response;
    } catch (error) {
      this.splunkLogger?.({
        logType: 'error',
        data: { ...splunk_message.ERROR.SET_PD_ERROR, error },
      });
      return error;
    }
  }
  async getPrescriptions(
    userDetailsDto: UserDetailsDto,
  ): Promise<{ active?: IRx[]; expired?: IRx[]; [key: string]: any } | { error: string }> {
    try {
      const response = await this.visionBffClient({
        method: 'POST',
        url: `/api/v1/user`,
        data: { query: PRESCRIPTIONS_QUERY, variables: userDetailsDto },
      });
      this.splunkLogger?.({
        logType: 'info',
        data: splunk_message.SUCCESS.PRESCRIPTION_LIST_SUCCESS,
      });
      if (response?.data?.errors) {
        return { error: 'getPrescriptions.standard.error' };
      }
      return safeGet(response, 'data.data.logInByGuid.prescriptions', { active: [], expired: [] });
    } catch (err: any) {
      this.splunkLogger?.({
        logType: 'error',
        data: { ...splunk_message.ERROR.PRESCRIPTION_LIST_ERROR, err },
      });
      console.log(err);
      if (err?.response?.status === 404) {
        return { error: 'getPrescriptions.404.error' };
      } else {
        return { error: 'getPrescriptions.standard.error' };
      }
    }
  }

  async getPrescriptionById(pid: string) {
    const graphqlQuery = {
      query: `
      fragment RxFields on Prescription {
        status
        type
        eyeglassRxType
        prescriptionId
        issueDate
        expirationDate
        prescribedBy
        prescriptionStrength
        prescriptionId
        leftEye {
            add
            axis
            bc
            color
            cyl
            dia
            model
            pd
            prisms {
                base
                prism
            }
            sku
            sph
        }
        rightEye {
            add
            axis
            bc
            color
            cyl
            dia
            model
            pd
            prisms {
                base
                prism
            }
            sku
            sph
        }
    }

    query ($prescriptionId: String!) {
        getPrescriptionById(prescriptionId: $prescriptionId) { ...RxFields }
      }
    `,
      variables: {
        forceCacheReload: false,
        prescriptionId: pid,
      },
    };
    try {
      const response = await this.visionBffClient({
        method: 'POST',
        url: `/api/v1/user`,
        data: graphqlQuery,
      });
      this.splunkLogger?.({
        logType: 'info',
        data: splunk_message.SUCCESS.PRESCRIPTION_BY_ID_SUCCESS,
      });
      return response?.data;
    } catch (error) {
      this.splunkLogger?.({
        logType: 'error',
        data: { ...splunk_message.ERROR.PRESCRIPTION_BY_ID_ERROR, error },
      });
      throw error;
    }
  }

  async sendBenefitRequest() {
    const graphqlQuery = {
      query: `
      query {
            sendBenefitRequest {
                logMsg
                reqBody
              }     
          }
    `,
      variables: {
        forceCacheReload: false,
      },
    };
    try {
      const response = await this.visionBffClient({
        method: 'POST',
        url: `/api/v1/user`,
        data: graphqlQuery,
      });
      this.splunkLogger?.({
        logType: 'info',
        data: splunk_message.SUCCESS.BENEFIT_REQUEST_SUCCESS,
      });
      return response?.data;
    } catch (error) {
      this.splunkLogger?.({
        logType: 'error',
        data: { ...splunk_message.ERROR.BENEFIT_REQUEST_ERROR, error },
      });
      throw error;
    }
  }

  async getExpireyToken() {
    const graphqlQuery = {
      query: `
      query {
        getTokenExpiry {
          expiresIn
              }     
          }
    `,
      variables: {
        forceCacheReload: false,
      },
    };
    try {
      const response = await this.visionBffClient({
        method: 'POST',
        url: `/api/v1/user`,
        data: graphqlQuery,
      });

      if (!response) {
        this.splunkLogger?.({
          logType: 'error',
          data: {
            ...splunk_message.ERROR.TOKEN_EXPIRY_EMPTY_RESPONSE,
          },
        });
        throw new Error('Empty response from token expiry service');
      }

      if (response?.data?.errors) {
        this.splunkLogger?.({
          logType: 'error',
          data: {
            ...splunk_message.ERROR.TOKEN_EXPIRY_GRAPHQL_ERROR,
            responseErrors: response.data.errors,
          },
        });
        throw new Error('GraphQL error in token expiry response');
      }

      const expiresIn = response?.data?.data?.getTokenExpiry?.expiresIn;

      if (expiresIn == null || typeof expiresIn !== 'number' || isNaN(expiresIn)) {
        this.splunkLogger?.({
          logType: 'error',
          data: {
            ...splunk_message.ERROR.TOKEN_EXPIRY_INVALID_VALUE,
            expiresIn,
          },
        });
        throw new Error('Invalid expiresIn value from token expiry service');
      }

      return expiresIn;
    } catch (error: any) {
      this.splunkLogger?.({
        logType: 'error',
        data: {
          ...splunk_message.ERROR.TOKEN_EXPIRY_ERROR,
          code: error?.code,
          message: error?.message,
          status: error?.response?.status,
          responseErrors: error?.response?.data?.errors,
          error,
        },
      });
      throw error;
    }
  }
}

export const BASE_USER_QUERY = gql`
  fragment RxFields on Prescription {
    status
    type
    eyeglassRxType
    prescriptionId
    issueDate
    expirationDate
    prescribedBy
    prescriptionStrength
    prescriptionId
    leftEye {
      add
      axis
      bc
      color
      cyl
      dia
      model
      pd
      prisms {
        base
        prism
      }
      sku
      sph
      skus {
        packSize
        colour
        sku
        webEnabled
      }
    }
    rightEye {
      add
      axis
      bc
      color
      cyl
      dia
      model
      pd
      prisms {
        base
        prism
      }
      sku
      sph
      skus {
        packSize
        colour
        sku
        webEnabled
      }
    }
  }

  query ($forceCacheReload: Boolean!) {
    logInByGuid {
      guid
      mrn
      region
      lastName
      firstName
      dob
      isA2Error
      phone
      ctCustomerId
      ctCustomerEmail
      isBenefitsOutOfSync
      email
      medixPlanTypes
      acuitasHome
      acuitasId
      prescriptions(forceCacheReload: $forceCacheReload) {
        active {
          ...RxFields
        }
        expired {
          ...RxFields
        }
      }
    }
  }
`;

export const EXTENDED_USER_QUERY = gql`
  fragment RxFields on Prescription {
    status
    type
    eyeglassRxType
    prescriptionId
    issueDate
    expirationDate
    prescribedBy
    prescriptionStrength
    prescriptionId
    leftEye {
      add
      axis
      bc
      color
      cyl
      dia
      model
      pd
      prisms {
        base
        prism
      }
      sku
      sph
      skus {
        packSize
        colour
        sku
        webEnabled
      }
    }
    rightEye {
      add
      axis
      bc
      color
      cyl
      dia
      model
      pd
      prisms {
        base
        prism
      }
      sku
      sph
      skus {
        packSize
        colour
        sku
        webEnabled
      }
    }
  }

  fragment BenefitFields on Benefit {
    optcode
    name
    id
    type
    isAvailable
    isECommerceEnabled
    extras
    coverage {
      product
      eligible
      frequencyInMonths
      amountAllotted
      amountUsed
      amountAvailable
      lastUsedDate
      nextAvailableDate
      isAvailable
    }
    medixPlanType
  }

  fragment AddressFields on Address {
    isDefault
    addressName
    addressType
    streetNumber
    streetName
    city
    state
    zipcode
    country
    firstName
    lastName
  }

  query ($forceCacheReload: Boolean!) {
    getUser {
      guid
      mrn
      region
      lastName
      firstName
      dob
      isA2Error
      isCartLoaded
      phone
      ctCustomerId
      ctCustomerEmail
      isBenefitsOutOfSync
      email
      acuitasHome
      acuitasId
      memberDetailLoading
      memberDetailsFound {
        error
        benefits
        warranties
        notifications
      }
      address {
        ...AddressFields
      }
      medixPlanTypes
      benefits(forceCacheReload: $forceCacheReload) {
        ...BenefitFields
      }
      prescriptions(forceCacheReload: $forceCacheReload) {
        active {
          ...RxFields
        }
        expired {
          ...RxFields
        }
      }
      # notifications {
      #     refillReminder
      #     receiveMarketing
      # }
      warranties(forceCacheReload: $forceCacheReload) {
        type
        status
        expirationDate
      }
      liveSession {
        eg_pd {
          distance {
            left
            right
          }
          intermediate {
            left
            right
          }
          near {
            left
            right
          }
          isEmpty
          sourcePD
        }
        eg_rx {
          status
          type
          eyeglassRxType
          prescriptionId
          issueDate
          expirationDate
          prescribedBy
          prescriptionStrength
          prescriptionId
          leftEye {
            add
            axis
            bc
            color
            cyl
            dia
            model
            pd
            prisms {
              base
              prism
            }
            sku
            sph
          }
          rightEye {
            add
            axis
            bc
            color
            cyl
            dia
            model
            pd
            prisms {
              base
              prism
            }
            sku
            sph
          }
        }
        cl_rx {
          status
          type
          eyeglassRxType
          prescriptionId
          issueDate
          expirationDate
          prescribedBy
          prescriptionStrength
          prescriptionId
          leftEye {
            add
            axis
            bc
            color
            cyl
            dia
            model
            pd
            prisms {
              base
              prism
            }
            skus {
              packSize
              colour
              sku
              webEnabled
            }
            sku
            sph
          }
          rightEye {
            add
            axis
            bc
            color
            cyl
            dia
            model
            pd
            prisms {
              base
              prism
            }
            sku
            skus {
              packSize
              colour
              sku
              webEnabled
            }
            sph
          }
        }
      }
    }
  }
`;
